import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, User, Building2, ArrowLeft, Eye, EyeOff, Mail, Loader2 } from 'lucide-react';
import { PublicFooter } from '@/components/PublicFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const initMode = searchParams.get('mode') || 'login';
  const [mode, setMode] = useState(initMode);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Google onboarding state (passed from AuthCallback)
  const onboarding = location.state?.onboarding || false;
  const onboardingName = location.state?.name || '';
  const onboardingPresetRole = location.state?.preset_role || null;

  const [formData, setFormData] = useState({
    email: '', password: '', name: onboardingName, region_id: '', company_name: '',
  });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/regions`).then((r) => setRegions(r.data)).catch(() => {});
  }, []);

  // Show error toast when returning from a failed OAuth attempt
  useEffect(() => {
    if (location.state?.authError) {
      toast.error('Invalid email or password');
      // Clear the state so the toast doesn't re-show on re-render
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
    }
  }, []);

  // If arriving from Google onboarding, jump straight to role select
  useEffect(() => {
    if (onboarding && mode === 'google-onboarding') {
      if (onboardingPresetRole) {
        setSelectedRole(onboardingPresetRole);
        setMode('google-employer-form');
      }
      // else stays on google-onboarding which shows role chooser
    }
  }, []);

  // ── helpers ──
  const routeByRole = (user) => {
    if (user.role === 'regional_admin') navigate('/regional-admin', { state: { user }, replace: true });
    else if (user.role === 'employer') navigate('/employer/dashboard', { state: { user }, replace: true });
    else navigate('/dashboard', { state: { user }, replace: true });
  };

  const syncWithBackend = async (accessToken) => {
    const res = await axios.post(
      `${BACKEND_URL}/api/auth/supabase-session`,
      { access_token: accessToken },
      { withCredentials: true },
    );
    return res.data;
  };

  // ── Sign In ──
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (!error && data.session) {
        const result = await syncWithBackend(data.session.access_token);
        if (result.needs_onboarding) {
          // Supabase user exists but no app account yet
          navigate('/auth?mode=google-onboarding', {
            state: { onboarding: true, name: result.name, preset_role: result.preset_role },
            replace: true,
          });
          return;
        }
        toast.success('Welcome back!');
        routeByRole(result.user);
        return;
      }

      // Fallback to legacy bcrypt login
      try {
        const legacy = await axios.post(
          `${BACKEND_URL}/api/auth/login`,
          { email: formData.email, password: formData.password },
          { withCredentials: true },
        );
        toast.success('Welcome back!');
        routeByRole(legacy.data.user);
      } catch (legacyErr) {
        toast.error(legacyErr.response?.data?.detail || 'Invalid email or password');
      }
    } catch {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Sign Up (email/password — role already collected) ──
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Full name is required'); return; }
    if (!formData.email || !formData.email.includes('@')) { toast.error('Please enter a valid email'); return; }
    if (!formData.password || formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (selectedRole === 'employer') {
      if (!formData.company_name.trim()) { toast.error('Company name is required'); return; }
      if (!formData.region_id) { toast.error('Please select a region'); return; }
    }

    setLoading(true);
    try {
      // Backend owns confirmation email delivery (via Resend). Supabase
      // still owns user creation, token generation, and confirmation
      // validation. The action_link Supabase generates is the same PKCE
      // shape as today, so AuthCallback's exchangeCodeForSession keeps
      // working unchanged. Response is intentionally generic to preserve
      // unknown-/duplicate-email enumeration safety.
      await axios.post(`${BACKEND_URL}/api/auth/signup`, {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        name: formData.name.trim(),
        role: selectedRole,
        company_name: selectedRole === 'employer' ? formData.company_name.trim() : undefined,
        region_id: selectedRole === 'employer' ? formData.region_id : undefined,
        redirect_to: `${window.location.origin}/auth/callback`,
      });
      setMode('check-email');
    } catch {
      toast.error("We couldn't complete registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Google OAuth (identity only — onboarding handled separately) ──
  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) toast.error(error.message || 'Google sign-in failed');
  };

  // ── Forgot Password ──
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.email.includes('@')) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      // Backend owns the send (via Resend). Supabase still owns token
      // generation/validation; backend asks Supabase Admin for a recovery
      // link and emails it through Resend. Response is intentionally generic
      // to preserve unknown-email safety.
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: formData.email,
        redirect_to: `${window.location.origin}/auth/reset-password`,
      });
      setMode('check-email-reset');
    } catch {
      toast.error('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // ── Complete Google Onboarding ──
  const handleCompleteOnboarding = async (e) => {
    e.preventDefault();
    if (selectedRole === 'employer') {
      if (!formData.company_name.trim()) { toast.error('Company name is required'); return; }
      if (!formData.region_id) { toast.error('Please select a region'); return; }
    }
    if (!formData.name.trim()) { toast.error('Name is required'); return; }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Session expired. Please sign in again.');
        navigate('/auth');
        return;
      }

      const res = await axios.post(
        `${BACKEND_URL}/api/auth/complete-onboarding`,
        {
          access_token: session.access_token,
          role: selectedRole,
          name: formData.name.trim(),
          company_name: selectedRole === 'employer' ? formData.company_name.trim() : undefined,
          region_id: selectedRole === 'employer' ? formData.region_id : undefined,
        },
        { withCredentials: true },
      );

      toast.success('Account created!');
      routeByRole(res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Account setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ═══════════ RENDER MODES ═══════════

  const renderLogin = () => (
    <form onSubmit={handleLogin} className="space-y-5">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#222]">Welcome Back</h2>
        <p className="text-[#666] mt-2">Sign in to your account</p>
      </div>
      <div>
        <Label htmlFor="email" className="text-[#333] font-medium">Email</Label>
        <Input data-testid="login-email-input" id="email" type="email" value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="h-11 mt-1.5 border-[#ddd]" placeholder="you@example.com" required />
      </div>
      <div>
        <Label htmlFor="password" className="text-[#333] font-medium">Password</Label>
        <div className="relative mt-1.5">
          <Input data-testid="login-password-input" id="password"
            type={showPassword ? 'text' : 'password'} value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="h-11 pr-12 border-[#ddd]" placeholder="Enter password" required />
          <button type="button" onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#aaa] hover:text-[#666]">
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex justify-end mt-1.5">
          <button type="button" onClick={() => setMode('forgot-password')}
            className="text-xs text-brand hover:underline" data-testid="forgot-password-link">
            Forgot password?
          </button>
        </div>
      </div>
      <Button data-testid="login-submit-btn" type="submit" disabled={loading}
        className="w-full h-11 bg-brand hover:bg-brand-hover text-white btn-uppercase">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {loading ? 'Signing In...' : 'Sign In'}
      </Button>
      <Divider />
      <Button data-testid="google-login-btn" type="button" variant="outline"
        onClick={handleGoogleLogin} className="w-full h-11 border-[#ddd] text-[#444]">
        <GoogleIcon /> Continue with Google
      </Button>
      <div className="text-center pt-4">
        <p className="text-[#666]">
          Don't have an account?{' '}
          <button type="button" onClick={() => setMode('role-select')}
            className="text-brand font-semibold hover:underline" data-testid="switch-to-register-btn">Create Account</button>
        </p>
      </div>
    </form>
  );

  const renderRoleSelect = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#222]">I am a...</h2>
        <p className="text-[#666] mt-2">Select your account type to continue</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button data-testid="role-job-seeker-btn"
          onClick={() => { setSelectedRole('job_seeker'); setMode('register'); }}
          className="p-6 border-2 border-[#e0e0e0] rounded-lg hover:border-brand hover:bg-brand-light transition-all text-left group">
          <User className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-[#222] mb-2">Job Seeker</h3>
          <p className="text-[#666] text-sm">Build your resume and find civilian employment.</p>
        </button>
        <button data-testid="role-employer-btn"
          onClick={() => { setSelectedRole('employer'); setMode('register'); }}
          className="p-6 border-2 border-[#e0e0e0] rounded-lg hover:border-brand hover:bg-brand-light transition-all text-left group">
          <Building2 className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-[#222] mb-2">Employer</h3>
          <p className="text-[#666] text-sm">Hire qualified veterans and post job opportunities.</p>
        </button>
      </div>
      <div className="text-center pt-4 border-t border-[#eee] mt-6">
        <p className="text-[#666]">
          Already have an account?{' '}
          <button onClick={() => setMode('login')} className="text-brand font-semibold hover:underline"
            data-testid="switch-to-login-btn">Sign In</button>
        </p>
      </div>
    </div>
  );

  // ── Google Onboarding: role chooser (same "I am a..." UI) ──
  const renderGoogleOnboarding = () => (
    <div className="space-y-6" data-testid="google-onboarding-role-select">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#222]">Almost There!</h2>
        <p className="text-[#666] mt-2">Select your account type to complete setup</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button data-testid="google-role-job-seeker-btn"
          onClick={() => {
            setSelectedRole('job_seeker');
            handleCompleteOnboardingJobSeeker();
          }}
          className="p-6 border-2 border-[#e0e0e0] rounded-lg hover:border-brand hover:bg-brand-light transition-all text-left group">
          <User className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-[#222] mb-2">Job Seeker</h3>
          <p className="text-[#666] text-sm">Build your resume and find civilian employment.</p>
        </button>
        <button data-testid="google-role-employer-btn"
          onClick={() => {
            setSelectedRole('employer');
            setMode('google-employer-form');
          }}
          className="p-6 border-2 border-[#e0e0e0] rounded-lg hover:border-brand hover:bg-brand-light transition-all text-left group">
          <Building2 className="w-10 h-10 text-brand mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
          <h3 className="text-lg font-bold text-[#222] mb-2">Employer</h3>
          <p className="text-[#666] text-sm">Hire qualified veterans and post job opportunities.</p>
        </button>
      </div>
    </div>
  );

  // Job seeker: complete immediately (no extra fields needed)
  const handleCompleteOnboardingJobSeeker = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Session expired. Please sign in again.'); navigate('/auth'); return; }

      const res = await axios.post(
        `${BACKEND_URL}/api/auth/complete-onboarding`,
        { access_token: session.access_token, role: 'job_seeker', name: formData.name || onboardingName },
        { withCredentials: true },
      );
      toast.success('Account created!');
      routeByRole(res.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Account setup failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Google Onboarding: Employer form (company + region required) ──
  const renderGoogleEmployerForm = () => (
    <form onSubmit={handleCompleteOnboarding} className="space-y-5" data-testid="google-employer-form">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => setMode('google-onboarding')}
          className="text-[#aaa] hover:text-[#444]">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-[#222]">Complete Employer Setup</h2>
          <p className="text-[#666] mt-1 text-sm">Provide your company details to continue</p>
        </div>
      </div>
      <div>
        <Label className="text-[#333] font-medium">Full Name</Label>
        <Input data-testid="onboard-name-input" value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="h-11 mt-1.5 border-[#ddd]" placeholder="John Doe" required />
      </div>
      <div>
        <Label className="text-[#333] font-medium">Company Name</Label>
        <Input data-testid="onboard-company-input" value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          className="h-11 mt-1.5 border-[#ddd]" placeholder="Your Company Inc." required />
      </div>
      <div>
        <Label className="text-[#333] font-medium">County/Region</Label>
        <select data-testid="onboard-region-select" value={formData.region_id}
          onChange={(e) => setFormData({ ...formData, region_id: e.target.value })}
          className="flex h-11 w-full rounded-md border border-[#ddd] bg-white px-3 py-2 text-sm mt-1.5" required>
          <option value="">Select your county/region</option>
          {regions.map((r) => (
            <option key={r.region_id} value={r.region_id}>{r.name}, {r.state}</option>
          ))}
        </select>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> Employer accounts require regional admin approval before you can post jobs.
        </p>
      </div>
      <Button data-testid="onboard-submit-btn" type="submit" disabled={loading}
        className="w-full h-11 bg-brand hover:bg-brand-hover text-white btn-uppercase">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {loading ? 'Setting Up...' : 'Complete Setup'}
      </Button>
    </form>
  );

  const renderRegister = () => (
    <form onSubmit={handleRegister} className="space-y-5">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => { setMode('role-select'); setSelectedRole(null); }}
          className="text-[#aaa] hover:text-[#444]" data-testid="back-to-role-select-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-[#222]">
            Create {selectedRole === 'employer' ? 'Employer' : 'Job Seeker'} Account
          </h2>
          <p className="text-[#666] mt-1 text-sm">
            {selectedRole === 'employer' ? 'Request access to post jobs' : 'Build your resume and find opportunities'}
          </p>
        </div>
      </div>
      <div>
        <Label className="text-[#333] font-medium">Full Name</Label>
        <Input data-testid="register-name-input" value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="h-11 mt-1.5 border-[#ddd]" placeholder="John Doe" required />
      </div>
      <div>
        <Label className="text-[#333] font-medium">Email</Label>
        <Input data-testid="register-email-input" type="email" value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="h-11 mt-1.5 border-[#ddd]" placeholder="you@example.com" required />
      </div>
      <div>
        <Label className="text-[#333] font-medium">Password</Label>
        <div className="relative mt-1.5">
          <Input data-testid="register-password-input"
            type={showPassword ? 'text' : 'password'} value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="h-11 pr-12 border-[#ddd]" placeholder="Min 8 characters" required />
          <button type="button" onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#aaa] hover:text-[#666]">
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {selectedRole === 'employer' && (
        <>
          <div>
            <Label className="text-[#333] font-medium">Company Name</Label>
            <Input data-testid="register-company-input" value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="h-11 mt-1.5 border-[#ddd]" placeholder="Your Company Inc." required />
          </div>
          <div>
            <Label className="text-[#333] font-medium">County/Region</Label>
            <select data-testid="register-region-select" value={formData.region_id}
              onChange={(e) => setFormData({ ...formData, region_id: e.target.value })}
              className="flex h-11 w-full rounded-md border border-[#ddd] bg-white px-3 py-2 text-sm mt-1.5" required>
              <option value="">Select your county/region</option>
              {regions.map((r) => (
                <option key={r.region_id} value={r.region_id}>{r.name}, {r.state}</option>
              ))}
            </select>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Employer accounts require regional admin approval before you can post jobs.
            </p>
          </div>
        </>
      )}
      <Button data-testid="register-submit-btn" type="submit" disabled={loading}
        className="w-full h-11 bg-brand hover:bg-brand-hover text-white btn-uppercase">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {loading ? 'Creating Account...' : 'Create Account'}
      </Button>
      <Divider />
      <Button data-testid="google-register-btn" type="button" variant="outline"
        onClick={handleGoogleLogin} className="w-full h-11 border-[#ddd] text-[#444]">
        <GoogleIcon /> Sign up with Google
      </Button>
      <div className="text-center pt-4">
        <p className="text-[#666]">
          Already have an account?{' '}
          <button type="button" onClick={() => setMode('login')}
            className="text-brand font-semibold hover:underline" data-testid="register-switch-to-login-btn">Sign In</button>
        </p>
      </div>
    </form>
  );

  const renderForgotPassword = () => (
    <form onSubmit={handleForgotPassword} className="space-y-5">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => setMode('login')} className="text-[#aaa] hover:text-[#444]">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-[#222]">Reset Password</h2>
          <p className="text-[#666] mt-1 text-sm">Enter your email and we'll send a reset link</p>
        </div>
      </div>
      <div>
        <Label className="text-[#333] font-medium">Email</Label>
        <Input data-testid="forgot-email-input" type="email" value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="h-11 mt-1.5 border-[#ddd]" placeholder="you@example.com" required />
      </div>
      <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
        className="w-full h-11 bg-brand hover:bg-brand-hover text-white btn-uppercase">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {loading ? 'Sending...' : 'Send Reset Link'}
      </Button>
      <div className="text-center pt-4">
        <p className="text-[#666]">
          Remember your password?{' '}
          <button type="button" onClick={() => setMode('login')}
            className="text-brand font-semibold hover:underline">Sign In</button>
        </p>
      </div>
    </form>
  );

  const renderCheckEmail = (isReset = false) => (
    <div className="text-center py-4" data-testid="check-email-view">
      <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
        <Mail className="w-8 h-8 text-brand" />
      </div>
      <h2 className="text-2xl font-bold text-[#222] mb-3">Check Your Email</h2>
      <p className="text-[#666] mb-6 leading-relaxed">
        {isReset
          ? <>We sent a password reset link to <strong>{formData.email}</strong>.</>
          : <>We sent a verification link to <strong>{formData.email}</strong>. Click the link to verify your account.</>
        }
      </p>
      <p className="text-xs text-[#999] mb-6">Didn't receive it? Check your spam folder.</p>
      <Button variant="outline" onClick={() => setMode('login')} className="border-[#ddd] text-[#444]"
        data-testid="back-to-login-btn">Back to Sign In</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f7f7] flex flex-col">
      <nav className="bg-white border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14">
            <a href="/" className="flex items-center gap-2.5">
              <img src={`${process.env.PUBLIC_URL}/sergeant-tap-logo.png`} alt="Sergeant TAP" className="h-8 w-auto object-contain" />
            </a>
          </div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 border border-[#e0e0e0] shadow-sm bg-white" data-testid="auth-card">
          {mode === 'login' && renderLogin()}
          {mode === 'role-select' && renderRoleSelect()}
          {mode === 'register' && renderRegister()}
          {mode === 'forgot-password' && renderForgotPassword()}
          {mode === 'check-email' && renderCheckEmail(false)}
          {mode === 'check-email-reset' && renderCheckEmail(true)}
          {mode === 'google-onboarding' && renderGoogleOnboarding()}
          {mode === 'google-employer-form' && renderGoogleEmployerForm()}
        </Card>
      </div>
      <PublicFooter />
    </div>
  );
}

function Divider() {
  return (
    <div className="relative my-5">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#e5e5e5]" /></div>
      <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-[#999]">or</span></div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default AuthPage;
